import {
  Container,
  TextField,
  Button,
  Typography,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Divider,
  Box,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import Header from "../header/header";
import {
  Exchange,
  ProprAccountOption,
  SettingsAccount,
  SettingsAccounts,
  useAPI,
} from "../../api/useAPI";
import ProprAccountPicker from "../../components/ProprAccountPicker";

/**
 * Propr caps leverage per asset: 5x on BTC/ETH, 2x on everything else. The
 * order-script applies one account-wide leverage to every order, so a Propr
 * account is pinned at 5x (the backend enforces this too).
 */
const PROPR_LEVERAGE = 5;

export default function AddProfileScreen() {
  const [exchange, setExchange] = useState<Exchange>("okx");
  const [idName, setIdName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [settingsFile, setSettingsFile] = useState("");
  const [buyCsv, setBuyCsv] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [disableLong, setDisableLong] = useState<boolean>(false);
  const [disableShort, setDisableShort] = useState<boolean>(false);

  // Propr-only: the account to trade on has to be chosen from the list the API
  // key can reach — it cannot be typed from memory.
  const [proprAccountId, setProprAccountId] = useState("");
  const [proprOptions, setProprOptions] = useState<ProprAccountOption[]>([]);
  const [proprLoading, setProprLoading] = useState(false);
  const [proprError, setProprError] = useState("");

  const { getSettingsAccounts, addAccount, getProprAccountsForKey } = useAPI();

  const isOkx = exchange === "okx";
  const isPropr = exchange === "propr";
  const isProprBetaKey = apiSecret.includes("pk_beta");

  const [settingsAccounts, setSettingsAccounts] = useState<SettingsAccounts>(
    {},
  );

  const settingsAccountOptions = useMemo(
    () => Object.keys(settingsAccounts),
    [settingsAccounts],
  );

  const selectSettingCsv = useCallback(
    (settingsFile: string) => {
      const settings: SettingsAccount = settingsAccounts[settingsFile];

      if (!settings) return;

      setIdName(settings.name);
      setDisplayName(settings.name);
      setSettingsFile(settings.settingsCsv);
      setBuyCsv(settings.executeCsv);

      // The settings CSV holds a Hyperliquid/OKX credential (masked). Propr
      // authenticates with its own API key typed in below, so do not prefill
      // these — a masked secret would otherwise land in the Propr key field.
      if (!isPropr) {
        setWalletAddress(settings.walletAddress);
        setApiSecret(settings.apiSecret);
      }
    },
    [settingsAccounts, isPropr],
  );

  useEffect(() => {
    getSettingsAccounts().then((accountsResponse) => {
      if (accountsResponse.success) {
        setSettingsAccounts(accountsResponse.data);
        return;
      }

      setMessage("Error: " + accountsResponse.error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switching exchange invalidates any Propr selection made before.
  useEffect(() => {
    setProprAccountId("");
    setProprOptions([]);
    setProprError("");
  }, [exchange]);

  const loadProprAccounts = useCallback(async () => {
    setProprLoading(true);
    setProprError("");
    setProprOptions([]);
    setProprAccountId("");

    try {
      const result = await getProprAccountsForKey(apiSecret.trim());

      if (result.success) {
        setProprOptions(result.data);
        if (result.data.length === 0) {
          setProprError("This API key has no Propr accounts.");
        }
      } else {
        setProprError(result.error || "Failed to load Propr accounts");
      }
    } catch (err) {
      setProprError(
        err instanceof Error ? err.message : "Failed to load Propr accounts",
      );
    } finally {
      setProprLoading(false);
    }
  }, [apiSecret, getProprAccountsForKey]);

  const selectedProprOption = proprOptions.find(
    (option) => option.accountId === proprAccountId,
  );
  const selectedIsFunded = selectedProprOption?.source === "funded";

  // A Propr account is unusable without an accountId: createConnectorFromBotAccount
  // identifies the exchange by that field, so it must be chosen before creating.
  const missingProprAccount = isPropr && !proprAccountId;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (missingProprAccount) {
      setMessage("Error: select a Propr account first");
      return;
    }

    setLoading(true);
    setMessage("");

    const createAccountBody = {
      exchange,
      // Propr's key is always typed in, never read from the settings CSV.
      secretFromCsv: isPropr ? false : apiSecret.includes("*"),
      idName: idName,
      displayName: displayName,
      walletAddress: isPropr ? "" : walletAddress,
      apiSecret: apiSecret,
      passphrase: isOkx ? passphrase : undefined,
      proprAccountId: isPropr ? proprAccountId : undefined,
      settingsCsv: settingsFile,
      executeCsv: buyCsv,
      settings: {
        disableLong,
        disableShort,
        ...(isPropr ? { sportMarginLeverage: PROPR_LEVERAGE } : {}),
      },
    };

    addAccount(createAccountBody).then((createAccount) => {
      setLoading(false);
      if (createAccount.success) {
        setMessage(`Created account '${displayName}'`);
        return;
      }
      setMessage(`Error during creation: ${createAccount.error}`);
    });
  };

  return (
    <Container maxWidth="xl">
      <Header back />
      <form onSubmit={handleSubmit}>
        <TextField
          select
          label="Exchange"
          value={exchange}
          onChange={(e) => setExchange(e.target.value as Exchange)}
          fullWidth
          margin="normal"
        >
          <MenuItem value="okx">OKX</MenuItem>
          <MenuItem value="hyperliquid">Hyperliquid</MenuItem>
          <MenuItem value="propr">Propr</MenuItem>
        </TextField>
        <Autocomplete
          // freeSolo
          onChange={(_e, value) => selectSettingCsv(value!)}
          options={settingsAccountOptions.map((option) => option)}
          renderInput={(params) => (
            <TextField {...params} label="Settings CSV file" required />
          )}
        />
        <TextField
          label="ID name"
          helperText="must be unique"
          value={idName}
          onChange={(e) => setIdName(e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          fullWidth
          margin="normal"
        />
        {!isPropr && (
          <TextField
            label={isOkx ? "API Key" : "Wallet Address"}
            helperText={isOkx ? "OKX API key" : "starts with 0x"}
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
            fullWidth
            margin="normal"
          />
        )}
        <TextField
          label={isPropr ? "Propr API key" : "API secret key"}
          helperText={
            isPropr
              ? "starts with pk_live_ (live) or pk_beta_ (beta sandbox)"
              : isOkx
                ? "OKX API secret"
                : "starts with 0x"
          }
          value={apiSecret}
          onChange={(e) => setApiSecret(e.target.value)}
          fullWidth
          margin="normal"
        />
        {isOkx && (
          <TextField
            label="Passphrase"
            helperText="OKX API passphrase"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            fullWidth
            margin="normal"
          />
        )}

        {isPropr && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Button
                variant="outlined"
                onClick={loadProprAccounts}
                disabled={proprLoading || apiSecret.trim() === ""}
              >
                {proprLoading ? "Loading..." : "Load Propr accounts"}
              </Button>
              {apiSecret.trim() !== "" && (
                <Chip
                  size="small"
                  color={isProprBetaKey ? "warning" : "error"}
                  variant="outlined"
                  label={
                    isProprBetaKey ? "beta sandbox key" : "LIVE key"
                  }
                />
              )}
            </Box>

            <TextField
              label="Leverage"
              value={PROPR_LEVERAGE}
              helperText="Fixed at 5x — Propr only allows 5x on BTC/ETH (other coins cap at 2x)"
              disabled
              fullWidth
              margin="normal"
            />

            {proprLoading && (
              <Box sx={{ textAlign: "center", my: 3 }}>
                <CircularProgress />
              </Box>
            )}

            {proprError !== "" && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {proprError}
              </Alert>
            )}

            {selectedIsFunded && (
              <Alert severity="error" sx={{ mb: 2 }}>
                This is a <strong>funded</strong> account: it trades{" "}
                <strong>real capital</strong>, not simulated funds. Orders are
                rejected until KYC is complete.
              </Alert>
            )}

            {proprOptions.length > 0 && (
              <>
                <Typography color="textSecondary" variant="body2" sx={{ mb: 1 }}>
                  Select the account this bot should trade on. Failed and closed
                  accounts cannot be selected.
                </Typography>
                <ProprAccountPicker
                  options={proprOptions}
                  selected={proprAccountId}
                  onSelect={setProprAccountId}
                />
              </>
            )}
          </Box>
        )}

        <TextField
          label="Buy csv file name"
          value={buyCsv}
          onChange={(e) => setBuyCsv(e.target.value)}
          fullWidth
          margin="normal"
        />

        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                defaultChecked={true}
                onChange={(e) => setDisableLong(!e.target.checked)}
                slotProps={{
                  input: { "aria-label": "controlled" },
                }}
              />
            }
            label="Enable Long positions"
          />

          <FormControlLabel
            control={
              <Checkbox
                defaultChecked={true}
                onChange={(e) => setDisableShort(!e.target.checked)}
                slotProps={{
                  input: { "aria-label": "controlled" },
                }}
              />
            }
            label="Enable Short positions"
          />
        </Box>

        <Divider sx={{ mb: 2, mt: 1 }} />

        <Button
          type="submit"
          variant="contained"
          disabled={loading || missingProprAccount}
        >
          {loading ? "Submitting..." : "Create Account"}
        </Button>
        {missingProprAccount && (
          <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
            Load your Propr accounts and select one to continue.
          </Typography>
        )}
        {message && (
          <Typography
            color={message.includes("Error") ? "error" : "success"}
            sx={{ mt: 2 }}
          >
            {message}
          </Typography>
        )}
      </form>
    </Container>
  );
}
